import { useState, useEffect } from "react";
import { Bookmark, Trash2, Search, ChevronDown, ChevronUp, ExternalLink, Copy } from "lucide-react";
import { GeneratedDork } from "@/types/dork";
import { toast } from "@/hooks/use-toast";
import { useLocale } from "@/contexts/LocaleContext";

interface SavedDorkCollection {
  id: string;
  keyword: string;
  dorks: GeneratedDork[];
  savedAt: Date;
  notes?: string;
}

const STORAGE_KEY = "saved_dorks_library";

export function SavedDorksLibrary() {
  const { t } = useLocale();
  const [isExpanded, setIsExpanded] = useState(false);
  const [collections, setCollections] = useState<SavedDorkCollection[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setCollections(
          parsed.map((c: any) => ({
            ...c,
            savedAt: new Date(c.savedAt),
          }))
        );
      } catch {
        console.error("Failed to parse saved dorks");
      }
    }
  }, []);

  const filteredCollections = collections.filter(
    (c) =>
      c.keyword.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.dorks.some((d) => d.query.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const deleteCollection = (id: string) => {
    const updated = collections.filter((c) => c.id !== id);
    setCollections(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    toast({
      title: t("saved.deleted_title"),
      description: t("saved.deleted_desc"),
    });
  };

  const copyDork = async (query: string) => {
    await navigator.clipboard.writeText(query);
    toast({ title: t("dork_card.copied_title"), description: t("dork_card.copied_desc") });
  };

  const openInGoogle = (query: string) => {
    window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, "_blank");
  };

  if (collections.length === 0) return null;

  return (
    <div className="glass-card p-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
            <Bookmark className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{t("saved.title")}</h3>
            <p className="text-xs text-muted-foreground">{t("saved.subtitle", { count: String(collections.length) })}</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4 animate-fade-in">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("saved.search_placeholder")}
              className="w-full pl-10 pr-4 py-2 bg-cyber-darker border border-border/30 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
            />
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto">
            {filteredCollections.map((collection) => (
              <div
                key={collection.id}
                className="p-3 rounded-lg bg-cyber-darker border border-border/30"
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-foreground text-sm">{collection.keyword}</h4>
                    <p className="text-xs text-muted-foreground">
                      {t("saved.dorks_count", { count: String(collection.dorks.length) })} • {new Date(collection.savedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteCollection(collection.id)}
                    className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-2">
                  {collection.dorks.slice(0, 3).map((dork) => (
                    <div
                      key={dork.id}
                      className="flex items-center gap-2 p-2 rounded bg-muted/20 border border-border/20"
                    >
                      <code className="flex-1 text-xs font-mono text-foreground/80 truncate">
                        {dork.query}
                      </code>
                      <button
                        onClick={() => copyDork(dork.query)}
                        className="p-1 hover:bg-muted rounded transition-colors"
                      >
                        <Copy className="w-3 h-3 text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => openInGoogle(dork.query)}
                        className="p-1 hover:bg-muted rounded transition-colors"
                      >
                        <ExternalLink className="w-3 h-3 text-muted-foreground" />
                      </button>
                    </div>
                  ))}
                  {collection.dorks.length > 3 && (
                    <p className="text-xs text-muted-foreground text-center">
                      {t("saved.more_dorks", { count: String(collection.dorks.length - 3) })}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function saveDorksToLibrary(keyword: string, dorks: GeneratedDork[], notes?: string) {
  const stored = localStorage.getItem(STORAGE_KEY);
  const existing: SavedDorkCollection[] = stored ? JSON.parse(stored) : [];

  const newCollection: SavedDorkCollection = {
    id: `saved-${Date.now()}`,
    keyword,
    dorks,
    savedAt: new Date(),
    notes,
  };

  const updated = [newCollection, ...existing].slice(0, 50);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

  return newCollection;
}
